import { shutdown } from '@intercom/messenger-js-sdk';
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';
import type { JSX, ReactNode } from 'react';

import { updateAuthToken } from 'pkg/actions/auth';
import * as flashActions from 'pkg/actions/flashes';
import * as notificationActions from 'pkg/actions/notifications';

import * as endpoints from 'pkg/api/endpoints/auto';
import type * as models from 'pkg/api/models';
import asyncLocalStorage from 'pkg/asyncLocalStorage';
import { endSession } from 'pkg/auth/session_end';
import * as sdk from 'pkg/core/sdk';
import { crash } from 'pkg/errors/errors';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import * as ls from 'pkg/local_storage';
import { LoginTypes, storeLoginInfo } from 'pkg/local_storage/login-info';
import { resetNotifications } from 'pkg/notifications/context';
import * as routes from 'pkg/router/routes';
import { replaceState } from 'pkg/router/state';

import { resetClient } from 'components/analytics';

import { setApiCurrentUser, setApiToken } from 'core/api_auth';

interface AuthContextValue {
	authToken: string;
	checkedAuth: boolean;
	login: (
		res: { account: models.account.Account; token: string },
		redirect?: boolean
	) => void;
	logout: (redirectTo?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
	authToken: '',
	checkedAuth: false,
	login: () => {},
	logout: async () => {},
});

export function useAuth(): AuthContextValue {
	return useContext(AuthContext);
}

export function AuthProvider({
	children,
}: {
	children: ReactNode;
}): JSX.Element {
	const [authToken, setAuthToken] = useState('');
	const [checkedAuth, setCheckedAuth] = useState(false);

	useComponentDidMount(() => {
		const token = localStorage.getItem(ls.LocalStorageKeys.AuthToken) || '';

		if (token) {
			setApiToken(token);
			setAuthToken(token);
		} else {
			notificationActions.deregisterPush();
		}

		setCheckedAuth(true);
	});

	const login = useCallback(
		(
			res: { account: models.account.Account; token: string },
			redirect: boolean = true
		) => {
			updateAuthToken({ accountToken: res.token });
			setAuthToken(res.token);

			if (redirect) {
				replaceState(routes.Home());
			}
		},
		[]
	);

	const logout = useCallback(async (redirectTo: string = '/') => {
		endSession();

		await notificationActions.deregisterPush();

		ls.removeItems(
			ls.LocalStorageKeys.ActiveOrganizationId,
			ls.LocalStorageKeys.Organizations,
			ls.LocalStorageKeys.ActiveMembership,
			ls.LocalStorageKeys.CalendarFilters
		);

		asyncLocalStorage.removeItem(ls.LocalStorageKeys.AuthToken);
		sessionStorage.removeItem('avatar-step');
		setApiToken('');
		setApiCurrentUser('');
		await window.caches?.delete('attachments').catch(() => {});

		resetNotifications();

		shutdown();
		resetClient();
		setAuthToken('');

		window.history.pushState({}, '', redirectTo);
	}, []);

	const value = useMemo(
		() => ({ authToken, checkedAuth, login, logout }),
		[authToken, checkedAuth, login, logout]
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 *	`onEmailNotVerified` is called with the account's email when the sign-in is
 *	refused until that email is verified; the API has emailed it a code.
 */
export function usePasswordAuth(): (
	username: string,
	password: string,
	lang?: string,
	redirect?: boolean,
	onEmailNotVerified?: (email: string) => void
) => Promise<boolean> {
	const { login } = useAuth();

	return useCallback(
		async (
			username: string,
			password: string,
			lang?: string,
			redirect?: boolean,
			onEmailNotVerified?: (email: string) => void
		): Promise<boolean> => {
			const headers: { [key: string]: string } = {};

			if (lang) {
				headers['Accept-Language'] = lang;
			}

			const response = await sdk.post(
				endpoints.Auth.Login(),
				{},
				{ username, password },
				headers
			);

			const result = await response.json();

			if (response.ok) {
				storeLoginInfo(result.account, LoginTypes.Email);
				login(result, redirect);
				return true;
			}

			if (
				response.status === 403 &&
				result?.error === 'email_not_verified' &&
				onEmailNotVerified
			) {
				onEmailNotVerified(result.email);
			}

			return false;
		},
		[login]
	);
}

export function useAuthWithApple(): (
	accessToken: string,
	lang: string,
	redirect?: boolean
) => Promise<boolean> {
	const { login } = useAuth();

	return useCallback(
		async (
			accessToken: string,
			lang: string,
			redirect?: boolean
		): Promise<boolean> => {
			const response = await sdk.request(
				endpoints.Auth.Login() + '?medium=apple',
				'POST',
				{ authCode: accessToken },
				{ 'Accept-Language': lang }
			);

			const result = await response.json();

			if (response.ok) {
				storeLoginInfo(result.account, LoginTypes.Apple);
				login(result, redirect);
				return true;
			}

			flashActions.show({
				title: crash().title,
				message: result.error,
			});

			return false;
		},
		[login]
	);
}
